import { useCallback, useEffect, useRef, useState } from 'react';

import type { FlightClass, StatesResponse } from '@flightscanner/shared';

import { fetchStates } from './apiClient';

import { enrichAll, type RichAircraft } from './enrich';



export interface ViewState {

  bbox: { minLon: number; minLat: number; maxLon: number; maxLat: number };

  zoom: number;

  globalFilter?: 'mil' | null;

}



export interface LiveState {

  data: StatesResponse | null;

  aircraft: RichAircraft[];

  loading: boolean;

  refreshing: boolean;

  error: string | null;

  refresh: () => void;

  setView: (v: ViewState) => void;

}



export const POLL_MS = 10_000;

export const PAN_DEBOUNCE_MS = 400;

const CACHE_MAX_ENTRIES = 4;



interface CacheEntry {

  response: StatesResponse;

  aircraft: RichAircraft[];

}



export function viewKey(v: ViewState): string {

  const b = v.bbox;

  return `${b.minLon.toFixed(1)},${b.minLat.toFixed(1)},${b.maxLon.toFixed(1)},${b.maxLat.toFixed(1)}:${Math.round(v.zoom)}:${v.globalFilter ?? ''}`;

}



/** API mode when only the military class filter is active. */

export function globalFilterForClasses(classes: FlightClass[]): 'mil' | null {

  return classes.length === 1 && classes[0] === 'military' ? 'mil' : null;

}



function apiErrorMessage(err: unknown): string {

  const msg = String(err);

  if (msg.includes('404')) {

    return 'API route not found - set VITE_API_BASE or run the Worker on port 8787';

  }

  if (

    msg.includes('502') ||

    msg.includes('503') ||

    msg.includes('ECONNREFUSED') ||

    msg.includes('Failed to fetch') ||

    msg.includes('NetworkError')

  ) {

    return 'API offline - run `pnpm dev:api` (port 8787) alongside `pnpm dev:web`';

  }

  if (msg.includes('timed out')) return `${msg}. Try zooming in slightly or panning.`;

  if (msg.includes('429') || msg.includes('420')) {

    return 'Upstream rate limited - flights will appear in a few seconds';

  }

  return (err as Error).message || 'Failed to load flights';

}



function touchCache(map: Map<string, CacheEntry>, key: string): CacheEntry | null {

  const entry = map.get(key);

  if (!entry) return null;

  map.delete(key);

  map.set(key, entry);

  return entry;

}



function putCache(map: Map<string, CacheEntry>, key: string, response: StatesResponse, aircraft: RichAircraft[]) {
  if (aircraft.length === 0) {
    const existing = map.get(key);
    if (existing && existing.aircraft.length > 0) return;
  }
  if (map.has(key)) map.delete(key);
  map.set(key, { response, aircraft });
  while (map.size > CACHE_MAX_ENTRIES) {
    const oldest = map.keys().next().value;
    if (oldest) map.delete(oldest);
  }
}



export function useLiveStates(): LiveState {

  const viewRef = useRef<ViewState | null>(null);

  const viewKeyRef = useRef('');

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const abortRef = useRef<AbortController | null>(null);

  const hasViewRef = useRef(false);

  const inFlightRef = useRef(false);

  const requestIdRef = useRef(0);

  const hasAircraftRef = useRef(false);

  const responseCacheRef = useRef(new Map<string, CacheEntry>());



  const [data, setData] = useState<StatesResponse | null>(null);

  const [aircraft, setAircraft] = useState<RichAircraft[]>([]);

  const [loading, setLoading] = useState(true);

  const [refreshing, setRefreshing] = useState(false);

  const [error, setError] = useState<string | null>(null);



  const restoreFromCache = useCallback((entry: CacheEntry) => {

    setData(entry.response);

    setAircraft(entry.aircraft);

    hasAircraftRef.current = entry.aircraft.length > 0;

    setError(null);

    setLoading(false);

  }, []);



  const applyResponse = useCallback((res: StatesResponse, key: string) => {
    if (key !== viewKeyRef.current) return;

    const next = enrichAll(res.aircraft);

    if (next.length === 0) {
      // Empty responses are almost always upstream gaps, not an empty sky.
      // Keep showing whatever we already have rather than blanking the map.
      const cached = responseCacheRef.current.get(key);
      if (cached && cached.aircraft.length > 0) {
        setData(cached.response);
        setAircraft(cached.aircraft);
        hasAircraftRef.current = true;
        setError(null);
        return;
      }
      if (hasAircraftRef.current) {
        setError(null);
        return;
      }
      // Always apply the empty payload so providerErrors reach the HUD.
      setData(res);
      hasAircraftRef.current = false;
      setAircraft([]);
      setError(res.providerErrors?.[0] ?? null);
      return;
    }

    putCache(responseCacheRef.current, key, res, next);
    setData(res);
    hasAircraftRef.current = true;
    setAircraft(next);
    setError(null);
  }, []);



  const tick = useCallback(async () => {

    const view = viewRef.current;

    if (!view) return;



    const reqId = ++requestIdRef.current;

    const key = viewKey(view);

    abortRef.current?.abort();

    const ctrl = new AbortController();

    abortRef.current = ctrl;

    inFlightRef.current = true;



    const cached = touchCache(responseCacheRef.current, key);

    const hasDisplay = cached != null || hasAircraftRef.current;

    if (!hasDisplay) setLoading(true);

    else setRefreshing(true);



    try {

      const res = await fetchStates(view.bbox, view.zoom, { globalFilter: view.globalFilter, signal: ctrl.signal });

      if (reqId !== requestIdRef.current) return;

      applyResponse(res, key);

    } catch (err) {

      if (ctrl.signal.aborted) return;

      if (reqId !== requestIdRef.current) return;

      const fallback = touchCache(responseCacheRef.current, key);

      if (fallback && key === viewKeyRef.current) {

        restoreFromCache(fallback);

      } else {

        setError(apiErrorMessage(err));

      }

    } finally {

      if (reqId === requestIdRef.current) {

        inFlightRef.current = false;

        if (abortRef.current === ctrl) abortRef.current = null;

        setLoading(false);

        setRefreshing(false);

      }

    }

  }, [applyResponse, restoreFromCache]);



  const refresh = useCallback(() => {

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(tick, PAN_DEBOUNCE_MS);

  }, [tick]);



  const setView = useCallback(

    (v: ViewState) => {

      const key = viewKey(v);

      const first = !hasViewRef.current;

      const moved = key !== viewKeyRef.current;

      hasViewRef.current = true;

      viewRef.current = v;

      if (moved) {

        viewKeyRef.current = key;

        const cached = touchCache(responseCacheRef.current, key);

        if (cached) restoreFromCache(cached);

      }

      if (first) void tick();

      else if (moved) refresh();

    },

    [tick, refresh, restoreFromCache],

  );



  useEffect(() => {

    const id = setInterval(() => {

      if (viewRef.current && !inFlightRef.current && !document.hidden) void tick();

    }, POLL_MS);

    const onVisible = () => {
      if (!document.hidden && viewRef.current && !inFlightRef.current) void tick();
    };

    document.addEventListener('visibilitychange', onVisible);

    return () => {

      clearInterval(id);

      document.removeEventListener('visibilitychange', onVisible);

      if (debounceRef.current) clearTimeout(debounceRef.current);

      abortRef.current?.abort();

    };

  }, [tick]);



  return { data, aircraft, loading, refreshing, error, refresh, setView };

}


